import { z } from 'zod';
import { COMPARISONS } from '../features/dashboard/widgets/comparisons.ts';
import { EXPENSE_VIEWS } from '../features/finance/finance.ts';
import { HISTORY_KINDS, NO_HISTORY_FILTERS } from '../features/history/history.ts';
import { MAX_COPIES_PER_VARIANT, MAX_LABEL_PRODUCTS } from '../features/labels/labels.ts';
import { INVENTORY_TABS, MOVEMENT_STATES } from '../features/inventory/inventory.ts';
import { HANDOVER_STATUSES } from '../features/handover/handover.ts';
import { ORDER_VIEWS } from '../features/orders/orders.ts';
import { RETURN_STATUSES } from '../features/parcels/parcels.ts';
import { NOTIFICATION_FILTERS } from '../features/notifications/useNotifications.ts';
import { LAST_SETUP_STEP } from '../features/setup/setup-progress.ts';
import { PERIOD_PRESETS } from '../features/report-period.ts';

export type LoginSearch = { returnTo?: string | undefined };

const loginSearch = z.object({ returnTo: z.string().optional() });

export const validateLoginSearch = (search: Record<string, unknown>): LoginSearch =>
  loginSearch.parse(search);

const catalogStatus = z.enum(['active', 'archived']).catch('active');

export const CATEGORIES_DEFAULTS = { status: 'active' } as const;

export type CategoriesSearch = { status: 'active' | 'archived' };

const categoriesSearch = z.object({ status: catalogStatus.default(CATEGORIES_DEFAULTS.status) });

export const validateCategoriesSearch = (search: Record<string, unknown>): CategoriesSearch =>
  categoriesSearch.parse(search);

export const INVENTORY_DEFAULTS = { tab: 'levels', state: null, depot: null } as const;

const inventorySearch = z.object({
  tab: z.enum(INVENTORY_TABS).catch(INVENTORY_DEFAULTS.tab).default(INVENTORY_DEFAULTS.tab),
  state: z.enum(MOVEMENT_STATES).nullable().catch(null).default(INVENTORY_DEFAULTS.state),
  depot: z.uuid().nullable().catch(null).default(INVENTORY_DEFAULTS.depot),
});

export type InventorySearch = z.infer<typeof inventorySearch>;

export const validateInventorySearch = (search: Record<string, unknown>): InventorySearch =>
  inventorySearch.parse(search);

export const ORDERS_DEFAULTS = {
  view: 'all',
  order: null,
  carrier: null,
  governorate: null,
  company: null,
  period: null,
} as const;

const FILTER_VALUE_MAX_LENGTH = 40;

const filterValue = z.string().min(1).max(FILTER_VALUE_MAX_LENGTH).nullable().catch(null).default(null);

const ordersSearch = z.object({
  view: z.enum(ORDER_VIEWS).catch('all').default(ORDERS_DEFAULTS.view),
  order: z.uuid().nullable().catch(null).default(ORDERS_DEFAULTS.order),
  carrier: filterValue,
  governorate: filterValue,
  company: filterValue,
  period: z.enum(PERIOD_PRESETS).nullable().catch(null).default(ORDERS_DEFAULTS.period),
});

export type OrdersSearch = z.infer<typeof ordersSearch>;

export const validateOrdersSearch = (search: Record<string, unknown>): OrdersSearch =>
  ordersSearch.parse(search);

export const HANDOVER_DEFAULTS = { handover: null, carrier: null, status: null, period: null } as const;

const handoverSearch = z.object({
  handover: z.uuid().nullable().catch(null).default(HANDOVER_DEFAULTS.handover),
  carrier: filterValue,
  status: z.enum(HANDOVER_STATUSES).nullable().catch(null).default(HANDOVER_DEFAULTS.status),
  period: z.enum(PERIOD_PRESETS).nullable().catch(null).default(HANDOVER_DEFAULTS.period),
});

export type HandoverSearch = z.infer<typeof handoverSearch>;

export const validateHandoverSearch = (search: Record<string, unknown>): HandoverSearch =>
  handoverSearch.parse(search);

export const RETURNS_DEFAULTS = { carrier: null, governorate: null, company: null, period: null } as const;

const returnsSearch = z.object({
  carrier: filterValue,
  governorate: filterValue,
  company: filterValue,
  period: z.enum(PERIOD_PRESETS).nullable().catch(null).default(RETURNS_DEFAULTS.period),
});

export type ReturnsSearch = z.infer<typeof returnsSearch>;

export const validateReturnsSearch = (search: Record<string, unknown>): ReturnsSearch =>
  returnsSearch.parse(search);

export const MISSING_PARCELS_DEFAULTS = { ...RETURNS_DEFAULTS, order: null } as const;

const missingParcelsSearch = returnsSearch.extend({
  order: z.uuid().nullable().catch(null).default(MISSING_PARCELS_DEFAULTS.order),
});

export type MissingParcelsSearch = z.infer<typeof missingParcelsSearch>;

export const validateMissingParcelsSearch = (search: Record<string, unknown>): MissingParcelsSearch =>
  missingParcelsSearch.parse(search);

export const RETURNED_PARCELS_DEFAULTS = { ...MISSING_PARCELS_DEFAULTS, status: null } as const;

const returnedParcelsSearch = missingParcelsSearch.extend({
  status: z.enum(RETURN_STATUSES).nullable().catch(null).default(RETURNED_PARCELS_DEFAULTS.status),
});

export type ReturnedParcelsSearch = z.infer<typeof returnedParcelsSearch>;

export const validateReturnedParcelsSearch = (search: Record<string, unknown>): ReturnedParcelsSearch =>
  returnedParcelsSearch.parse(search);

export const CUSTOMERS_DEFAULTS = { governorate: null, city: null, customer: null } as const;

const AREA_MAX_LENGTH = 100;

const areaFilter = z.string().trim().min(1).max(AREA_MAX_LENGTH).nullable().catch(null);

const customersSearch = z.object({
  governorate: areaFilter.default(CUSTOMERS_DEFAULTS.governorate),
  city: areaFilter.default(CUSTOMERS_DEFAULTS.city),
  customer: z.uuid().nullable().catch(null).default(CUSTOMERS_DEFAULTS.customer),
});

export type CustomersSearch = z.infer<typeof customersSearch>;

export const validateCustomersSearch = (search: Record<string, unknown>): CustomersSearch =>
  customersSearch.parse(search);

export const HISTORY_DEFAULTS = NO_HISTORY_FILTERS;

const calendarDay = z.iso.date().nullable().catch(null);

const historySearch = z.object({
  kind: z.enum(HISTORY_KINDS).nullable().catch(null).default(null),
  actor: z.uuid().nullable().catch(null).default(null),
  from: calendarDay.default(null),
  to: calendarDay.default(null),
});

export type HistorySearch = z.infer<typeof historySearch>;

export const validateHistorySearch = (search: Record<string, unknown>): HistorySearch =>
  historySearch.parse(search);

const labelsSearch = z.object({
  product: z.array(z.uuid()).max(MAX_LABEL_PRODUCTS).catch([]).default([]),
  variant: z.uuid().nullable().catch(null).default(null),
  quantity: z.coerce.number().int().min(1).max(MAX_COPIES_PER_VARIANT).nullable().catch(null).default(null),
});

export type LabelsSearch = z.infer<typeof labelsSearch>;

export const LABELS_DEFAULTS: LabelsSearch = { product: [], variant: null, quantity: null };

export const validateLabelsSearch = (search: Record<string, unknown>): LabelsSearch =>
  labelsSearch.parse(search);

export const NOTIFICATIONS_DEFAULTS = { filter: 'all' } as const;

const notificationsSearch = z.object({
  filter: z.enum(NOTIFICATION_FILTERS).catch('all').default('all'),
});

export type NotificationsSearch = z.infer<typeof notificationsSearch>;

export const validateNotificationsSearch = (search: Record<string, unknown>): NotificationsSearch =>
  notificationsSearch.parse(search);

export const SETUP_DEFAULTS = { step: 0 } as const;

const setupSearch = z.object({
  step: z.coerce
    .number()
    .int()
    .min(0)
    .max(LAST_SETUP_STEP)
    .catch(SETUP_DEFAULTS.step)
    .default(SETUP_DEFAULTS.step),
});

export type SetupSearch = z.infer<typeof setupSearch>;

export const validateSetupSearch = (search: Record<string, unknown>): SetupSearch =>
  setupSearch.parse(search);

export const STATISTICS_DEFAULTS = { period: 'last_7_days', carrier: '', groupBy: '' } as const;

const statisticsSearch = z.object({
  period: z.enum(PERIOD_PRESETS).catch(STATISTICS_DEFAULTS.period).default(STATISTICS_DEFAULTS.period),
  carrier: z.string().max(64).catch(STATISTICS_DEFAULTS.carrier).default(STATISTICS_DEFAULTS.carrier),
  groupBy: z.uuid().or(z.literal('')).catch(STATISTICS_DEFAULTS.groupBy).default(STATISTICS_DEFAULTS.groupBy),
});

export type StatisticsSearch = z.infer<typeof statisticsSearch>;

export const validateStatisticsSearch = (search: Record<string, unknown>): StatisticsSearch =>
  statisticsSearch.parse(search);

export const EXPENSES_DEFAULTS = { view: 'active', company: null } as const;

const expensesSearch = z.object({
  view: z.enum(EXPENSE_VIEWS).catch(EXPENSES_DEFAULTS.view).default(EXPENSES_DEFAULTS.view),
  company: filterValue,
});

export type ExpensesSearch = z.infer<typeof expensesSearch>;

export const validateExpensesSearch = (search: Record<string, unknown>): ExpensesSearch =>
  expensesSearch.parse(search);

export const DASHBOARD_DEFAULTS = {
  company: null,
  product: null,
  origin: null,
  from: null,
  to: null,
  compare: 'none',
  view: null,
} as const;

const ORIGIN_FILTER = /^(local|imported|[A-Z]{2})$/u;

const dashboardSearch = z.object({
  company: filterValue,
  product: z.uuid().nullable().catch(null).default(DASHBOARD_DEFAULTS.product),
  origin: z.string().regex(ORIGIN_FILTER).nullable().catch(null).default(DASHBOARD_DEFAULTS.origin),
  from: calendarDay.default(DASHBOARD_DEFAULTS.from),
  to: calendarDay.default(DASHBOARD_DEFAULTS.to),
  compare: z.enum(COMPARISONS).catch(DASHBOARD_DEFAULTS.compare).default(DASHBOARD_DEFAULTS.compare),
  view: z.uuid().nullable().catch(null).default(DASHBOARD_DEFAULTS.view),
});

export type DashboardSearch = z.infer<typeof dashboardSearch>;

export const validateDashboardSearch = (search: Record<string, unknown>): DashboardSearch =>
  dashboardSearch.parse(search);
